import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export async function downloadDailyFeeReportPerFeeHeadPDF({
  rows,
  columns,
  fromDate,
  toDate,
  academicYear,
  feeType,
  feeHead,
  examType,
  collegeDetails,
  logoBase64,
  paymentStatus,
}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a3",
  });

  let startY = 10;
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const logoWidth = 28;
  const logoHeight = 28;
  const logoY = startY;
  let textBlockY = logoY + 2;
  let textX = pageWidth / 2;

  // Header: Logo left, text centered
  if (collegeDetails) {
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, "PNG", margin, logoY, logoWidth, logoHeight);
      } catch (e) {}
    }
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(collegeDetails.college_name?.toUpperCase() || "COLLEGE NAME", textX, textBlockY + 3, {
      align: "center",
    });
    // Autonomous + Accreditation
    const autoAcc = `${collegeDetails.is_autonomous ? "(AUTONOMOUS" : ""}${
      collegeDetails.accreditation
        ? (collegeDetails.is_autonomous ? "; " : "(") + collegeDetails.accreditation
        : ""
    }${collegeDetails.is_autonomous || collegeDetails.accreditation ? ")" : ""}`;
    if (autoAcc && autoAcc !== "") {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(autoAcc, textX, textBlockY + 12, { align: "center" });
    }
    if (collegeDetails.affiliation) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(collegeDetails.affiliation, textX, textBlockY + 20, { align: "center" });
    }
    // Report Title
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    // Title with Fee Head and Exam Type
    let dateRange = `(From: ${fromDate || "-"} To: ${toDate || "-"})`;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    // Compose title: Fee Head and Exam Type only
    let title = "";
    if (feeHead) title += feeHead;
    if (examType) title += (title ? " (" : "") + examType + (title ? ")" : "");
    if (!title) title = "Fee Report";
    const titleWidth = doc.getTextWidth(title);
    const dateRangeWidth = doc.getTextWidth(dateRange);
    const totalWidth = titleWidth + 6 + dateRangeWidth;
    const startX = textX - totalWidth / 2;
    doc.text(title, startX, textBlockY + 32, { align: "left" });
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(dateRange, startX + titleWidth + 6, textBlockY + 32, { align: "left" });
    // Date Range and Filters (below)
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    let filterInfo = `Academic Year: ${academicYear || "-"}  Fee Type: ${
      feeType || "-"
    }  Fee Head: ${feeHead || "-"}  Payment Status: ${paymentStatus || "All"}`;
    doc.text(filterInfo, textX, textBlockY + 39, { align: "center" });
    startY = logoY + logoHeight + 10;
  } else {
    // Fallback
    doc.setFontSize(16);
    doc.text("Daily Fee Report Per Fee Head", 14, 10);
    doc.setFontSize(11);
    let info = `From: ${fromDate || "-"}  To: ${toDate || "-"}  Academic Year: ${
      academicYear || "-"
    }  Fee Type: ${feeType || "-"}  Fee Head: ${feeHead || "-"}  Payment Status: ${
      paymentStatus || "All"
    }`;
    doc.text(info, 14, 18);
    startY = 25;
  }

  // Table columns
  const tableColumns = [
    "S. No.",
    "Student Name",
    "Roll No",
    "Receipt Date",
    "Actual Amount",
    "Paid Amount",
    "Remarks",
    "Payment Status",
  ];

  // Group rows by receipt_date
  const grouped = {};
  let grandTotal = 0;
  rows.forEach((row) => {
    const date = row.receipt_date ? new Date(row.receipt_date).toLocaleDateString() : "";
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(row);
    let amtStr = (row.paid_amount ?? "").toString().replace(/[\,\s]/g, "");
    const amt = parseFloat(amtStr) || 0;
    grandTotal += amt;
  });

  // Prepare tableRows with subtotals
  let tableRows = [];
  let serialNo = 1;
  Object.keys(grouped)
    .sort((a, b) => {
      // Parse MM/DD/YYYY to Date
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB;
    })
    .forEach((date) => {
      let dateTotal = 0;
      grouped[date].forEach((row) => {
        let amtStr = (row.paid_amount ?? "").toString().replace(/[\,\s]/g, "");
        dateTotal += parseFloat(amtStr) || 0;
        const paymentStatusText =
          row.fee_status === "paid"
            ? "Paid"
            : row.fee_status === "notpaid"
            ? "Not Paid"
            : row.fee_status || "-";
        tableRows.push([
          serialNo++,
          row["student_name"] || "",
          row["student_rollno"] || "",
          date,
          row["actual_amount"]
            ? `Rs. ${Number(row["actual_amount"]).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : "Rs. 0.00",
          row["paid_amount"]
            ? `Rs. ${Number(row["paid_amount"]).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : "Rs. 0.00",
          formatRemarksForColumn(row["remarks"] || "", 100),
          paymentStatusText,
        ]);
      });
      // Subtotal row for this date
      tableRows.push([
        "",
        {
          content: `Total for ${date}`,
          colSpan: 3,
          styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240] },
        },
        {
          content: `Rs. ${dateTotal.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
          colSpan: 1,
          styles: { halign: "right", fontStyle: "bold", fillColor: [240, 240, 240] },
        },
        "",
        "",
      ]);
    });

  // Grand total row
  tableRows.push([
    "",
    {
      content: "Grand Total",
      colSpan: 3,
      styles: { halign: "right", fontStyle: "bold", fillColor: [220, 220, 220] },
    },
    {
      content: `Rs. ${grandTotal.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      colSpan: 1,
      styles: { halign: "right", fontStyle: "bold", fillColor: [220, 220, 220] },
    },
    "",
    "",
  ]);

  const columnStyles = {
    0: { cellWidth: 15, halign: "center" }, // S. No.
    1: { cellWidth: 40, halign: "left" }, // Student Name
    2: { cellWidth: 35, halign: "center" }, // Roll No
    3: { cellWidth: 35, halign: "center" }, // Receipt Date
    4: { cellWidth: 25, halign: "right" }, // Actual Amount
    5: { cellWidth: 25, halign: "right" }, // Paid Amount
    6: { cellWidth: 100, halign: "left" }, // Remarks
    7: { cellWidth: 30, halign: "center" }, // Payment Status
  };

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: startY,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 9,
      halign: "center",
      valign: "middle",
      cellPadding: 3,
      lineWidth: 0.2,
    },
    columnStyles: columnStyles,
    theme: "grid",
    tableWidth: "auto",
    margin: { left: 14, right: 14 },
    didParseCell: function (data) {
      data.cell.styles.textColor = [0, 0, 0];
      if (data.column.index === 6) {
        data.cell.styles.fontSize = 6.5;
        data.cell.styles.overflow = "linebreak";
        data.cell.styles.minCellHeight = 8;
        data.cell.styles.cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
      }
      if (
        data.column.index === 0 ||
        data.column.index === 1 ||
        data.column.index === 2 ||
        data.column.index === 3 ||
        data.column.index === 7
      ) {
        data.cell.styles.halign = "center";
      }
      // Style payment status column with colors
      if (data.column.index === 7 && data.cell.raw) {
        const status = data.cell.raw;
        if (status === "Paid") {
          data.cell.styles.fillColor = [76, 175, 80];
          data.cell.styles.textColor = [255, 255, 255];
        } else if (status === "Not Paid") {
          data.cell.styles.fillColor = [244, 67, 54];
          data.cell.styles.textColor = [255, 255, 255];
        }
      }
    },
  });

  doc.save("DailyFeeReportPerFeeHead.pdf");
}

function formatRemarksForColumn(remarks, maxWidth) {
  if (!remarks) return "";
  const remarksStr = String(remarks);
  let formattedRemarks = remarksStr.replace(/,/g, ", ");
  const ids = formattedRemarks.split(",").map((id) => id.trim());
  const charsPerLine = Math.floor(maxWidth * 1.5);
  let result = [];
  let currentLine = "";

  ids.forEach((id, index) => {
    if (!id) return;
    if ((currentLine + (currentLine ? ", " : "") + id).length <= charsPerLine) {
      if (currentLine) {
        currentLine += ", " + id;
      } else {
        currentLine = id;
      }
    } else {
      if (currentLine) result.push(currentLine);
      currentLine = id;
    }
    if (index === ids.length - 1) result.push(currentLine);
  });

  return result.join("\n");
}
